import { useMemo } from "react";
import ReactCountryFlag from "react-country-flag";
// @mui
import { Box, Grid, LinearProgress, Typography } from "@mui/material";
// type
import { CountryTrafficGrowth } from "~src/types/brand";
// utils
import { parsePercentageValue } from "../../utils";
//components
import ChangeIndicator from "./ChangeIndicator";

// -----------------------------------------------------------------------------------

export default function CountryDetails({
  data,
}: {
  data?: CountryTrafficGrowth[][];
}) {
  function getTop5Traffic(
    data: CountryTrafficGrowth[][],
  ): CountryTrafficGrowth[] {
    // Merge the arrays
    const mergedData = data.flat();

    // Group elements by country code and keep the highest traffic for each code
    const groupedData: { [code: string]: CountryTrafficGrowth } = {};
    for (const item of mergedData) {
      const code = item.code;
      if (
        !(code in groupedData) ||
        parseFloat(item.traffic) > parseFloat(groupedData[code].traffic)
      ) {
        groupedData[code] = item;
      }
    }

    // Sort the grouped elements by traffic in descending order
    const sortedData = Object.values(groupedData).sort(
      (a, b) => parseFloat(b.traffic) - parseFloat(a.traffic),
    );

    // Take the top 5 elements
    return sortedData.slice(0, 5);
  }

  const countryList = useMemo(() => data && getTop5Traffic(data), [data]);

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          mt: 1,
          mb: 1
        }}>
        Top Countries
      </Typography>
      <Grid container sx={{ flexDirection: "column" }}>
        {countryList?.map((each) => (
          <Grid  sx={{ height: 42, mb: 2 }} key={each.country}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 2,
              }}
            >
              <ReactCountryFlag
                countryCode={each.code}
                svg
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: "20px",
                  fontWeight: 500,
                  ml: 2,
                  mr: 1,
                }}
              >
                {each.country}
              </Typography>
              {each.change && each.growth && <ChangeIndicator data={each} />}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-end",
                ml: "auto",
              }}
            >
              <LinearProgress
                sx={{
                  height: 10,
                  width: { xs: "100%", md: 215 },
                  borderRadius: 0,
                  "& .MuiLinearProgress-bar": { borderRadius: 0 },
                }}
                variant="determinate"
                value={each.traffic ? parsePercentageValue(each.traffic) : 0}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: "20px",
                  fontWeight: 500,
                  ml: 2,
                  position: "relative",
                  top: "5px",
                }}
              >
                {each.traffic}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </>
  );
}
